import { useEffect, useMemo, useState } from "react";

import { useURL } from "~/contexts";

import { fetchFeaturedProducts } from "./dynamic-product-tile.client";
import {
  featuredProductsPath,
  lookupFromBatch,
  pickFeaturedProducts,
  uniqueProductIds,
} from "./dynamic-product-tile.logic";
import { FEATURED_PRODUCTS_MAX_IDS } from "./dynamic-product-tile.schemas";
import type { FeaturedLookup, FeaturedPick } from "./types";

/**
 * Per-id outcomes for an id list, from ONE batched request; `null` while it
 * is in flight. The answer is keyed to the id list it was read for: when an
 * author edits the list the old answer is ignored (renders as loading, never
 * as the wrong products) and its read is aborted. Empty list → `{}`.
 */
export function useFeaturedLookup(productIds: Array<string | null>): {
  ids: string[];
  lookup: FeaturedLookup | null;
} {
  const url = useURL();
  // Capped to what the route reads, so rows past the cap are simply absent
  // rather than reported as "not found".
  const ids = uniqueProductIds(productIds).slice(0, FEATURED_PRODUCTS_MAX_IDS);
  const idsKey = ids.join(",");
  const [state, setState] = useState<{
    key: string;
    lookup: FeaturedLookup;
  } | null>(null);

  useEffect(() => {
    if (ids.length === 0) return;
    const controller = new AbortController();
    void fetchFeaturedProducts(
      url(featuredProductsPath(ids)),
      controller.signal,
    ).then(result => {
      if (controller.signal.aborted) return;
      setState({ key: idsKey, lookup: lookupFromBatch(ids, result) });
    });
    return () => controller.abort();
    // `ids` is captured via `idsKey`; `url` is rebuilt per SiteContext render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const lookup =
    ids.length === 0 ? {} : state?.key === idsKey ? state.lookup : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ ids, lookup }), [idsKey, lookup]);
}

/**
 * The first `count` in-stock products from an ordered id list; later ids are
 * backups that step in for an out-of-stock / missing earlier one.
 */
export function useFeaturedProducts(
  productIds: Array<string | null>,
  count: number,
): FeaturedPick {
  const { ids, lookup } = useFeaturedLookup(productIds);
  return useMemo(
    () => pickFeaturedProducts(ids, lookup, count),
    [ids, lookup, count],
  );
}
